import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight, ArrowLeft, Sparkles, User, Mail, Heart, Zap, Target,
  Moon, Check, Send, Shield, Star, ChevronRight, Activity, Brain,
  Eye, Footprints, Scale, Ruler, AlertCircle, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Logo } from '../components/Logo';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCREENS = {
  WELCOME: 'welcome',
  IDENTITY: 'identity',
  SNAPSHOT: 'snapshot',
  PROCESSING: 'processing',
  TWIN_REVEAL: 'twin_reveal',
  COACH_MESSAGE: 'coach_message',
  CLOSE: 'close',
};

const GOAL_OPTIONS = [
  { value: 'lose_weight', label: 'Lose Weight' },
  { value: 'build_muscle', label: 'Build Muscle' },
  { value: 'manage_stress', label: 'Manage Stress' },
  { value: 'sleep_better', label: 'Sleep Better' },
  { value: 'more_energy', label: 'More Energy' },
  { value: 'eat_healthier', label: 'Eat Healthier' },
  { value: 'general_wellness', label: 'General Wellness' },
];

const SLEEP_OPTIONS = [
  { value: 'poor', label: 'Poor', color: 'text-red-400' },
  { value: 'okay', label: 'Okay', color: 'text-amber-500' },
  { value: 'good', label: 'Good', color: 'text-emerald-500' },
];

const ACTIVITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'high', label: 'High' },
];

const CONDITION_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'diabetes', label: 'Diabetes' },
  { value: 'hypertension', label: 'Hypertension' },
  { value: 'other', label: 'Other' },
];

const PROCESSING_MESSAGES = [
  'Building your Digital Twin...',
  'Analyzing your sleep and energy patterns...',
  'Mapping your wellness profile...',
  'Generating your Coach360 insights...',
];

// ── Tile button ──
const Tile = ({ selected, onClick, children, className = '' }) => (
  <motion.button
    whileTap={{ scale: 0.97 }}
    onClick={onClick}
    className={`px-4 py-2.5 rounded-xl border-2 transition-all text-sm font-medium ${
      selected
        ? 'border-primary bg-primary/8 text-primary'
        : 'border-border bg-card hover:border-primary/30 text-foreground'
    } ${className}`}
  >
    {children}
  </motion.button>
);

// ── Mood quick-select (1-10) ──
const MoodSelect = ({ value, onChange }) => (
  <div className="flex flex-wrap gap-2 justify-center">
    {[1,2,3,4,5,6,7,8,9,10].map(n => (
      <button
        key={n}
        onClick={() => onChange(n)}
        className={`w-9 h-9 rounded-full text-sm font-bold transition-all ${
          value === n
            ? 'bg-primary text-primary-foreground scale-110 shadow-md'
            : 'bg-muted hover:bg-muted/80 text-muted-foreground'
        }`}
        data-testid={`mood-${n}`}
      >
        {n}
      </button>
    ))}
  </div>
);

const fadeSlide = {
  initial: { opacity: 0, x: 30 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -30 },
  transition: { duration: 0.25 },
};

// ── Main component ──
export default function DemoFlow() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login: authLogin, updateUser: authUpdate } = useAuth();
  const [screen, setScreen] = useState(SCREENS.WELCOME);
  const [isLoading, setIsLoading] = useState(false);
  const [processingIdx, setProcessingIdx] = useState(0);
  const [processingDone, setProcessingDone] = useState(false);

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    goal: '',
    energy: '',
    mood: 5,
    sleep_quality: '',
    activity_level: '',
    known_condition: '',
    stress: '',
    height: '',
    weight: '',
    source: searchParams.get('source') || 'qr_code',
    event: searchParams.get('event') || 'bwell_march28',
  });

  const [userId, setUserId] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);
  const [result, setResult] = useState(null); // { briefing, dts_score, vitals, briefing_token }
  const [emailSent, setEmailSent] = useState(false);

  const up = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const track = useCallback(async (evt) => {
    try { await axios.post(`${API}/demo/analytics`, { event: evt, session_token: sessionToken, user_id: userId }); } catch {}
  }, [sessionToken, userId]);

  // ── Identity submit ──
  const submitIdentity = async () => {
    if (!form.first_name.trim() || !form.email.trim()) { toast.error('Name and email are required'); return; }
    setIsLoading(true);
    try {
      const r = await axios.post(`${API}/demo/register`, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        source: form.source,
        event: form.event,
      });
      setUserId(r.data.user_id);
      setSessionToken(r.data.session_token);
      setScreen(SCREENS.SNAPSHOT);
    } catch { toast.error('Something went wrong. Please try again.'); }
    finally { setIsLoading(false); }
  };

  // ── Generate briefing ──
  const generateBriefing = async () => {
    setScreen(SCREENS.PROCESSING);
    setProcessingIdx(0);
    setProcessingDone(false);
    track('processing_started');
    try {
      const r = await axios.post(`${API}/demo/generate-briefing`, {
        user_id: userId,
        session_token: sessionToken,
        intake: {
          goal: form.goal,
          energy: form.energy,
          mood: form.mood,
          sleep_quality: form.sleep_quality,
          activity_level: form.activity_level,
          known_condition: form.known_condition,
          stress: form.stress,
          height: form.height,
          weight: form.weight,
        },
      });
      setResult(r.data);
      setProcessingDone(true);
    } catch (err) {
      console.error(err);
      toast.error('Generation failed — please try again.');
      setScreen(SCREENS.SNAPSHOT);
    }
  };

  // ── Processing animation ──
  useEffect(() => {
    if (screen !== SCREENS.PROCESSING) return;
    const iv = setInterval(() => setProcessingIdx(p => Math.min(p + 1, PROCESSING_MESSAGES.length - 1)), 1200);
    return () => clearInterval(iv);
  }, [screen]);

  useEffect(() => {
    if (screen !== SCREENS.PROCESSING || !processingDone) return;
    const t = setTimeout(() => { setScreen(SCREENS.TWIN_REVEAL); track('twin_revealed'); }, 1800);
    return () => clearTimeout(t);
  }, [screen, processingDone, track]);

  // ── Send email ──
  const sendReport = async () => {
    setIsLoading(true);
    try {
      await axios.post(`${API}/demo/send-link`, {
        user_id: userId, session_token: sessionToken, method: 'email', email: form.email,
      });
      track('email_sent');
      toast.success('Report sent to your email!');
      setEmailSent(true);
    } catch { toast.error('Failed to send — try again.'); }
    finally { setIsLoading(false); }
  };

  // ── helpers ──
  const dts = result?.dts_score || 0;
  const vitals = result?.vitals || {};
  const briefing = result?.briefing || {};

  const getDtsColor = (s) => {
    if (s >= 82) return '#10b981';
    if (s >= 76) return '#f59e0b';
    return '#ef4444';
  };

  // ───────────────────────────────────────────────
  const renderScreen = () => {
    switch (screen) {

      // ── 1. WELCOME ──
      case SCREENS.WELCOME:
        return (
          <motion.div {...fadeSlide} key="welcome" className="min-h-screen flex flex-col items-center justify-center p-6 text-center">
            <Logo className="h-12 mb-8" />
            <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.2 }}>
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary/20 to-cyan-500/20 flex items-center justify-center mx-auto mb-6">
                <Activity size={40} className="text-primary" />
              </div>
            </motion.div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-3" data-testid="welcome-title">Your Digital Twin Awaits</h1>
            <p className="text-muted-foreground max-w-xs mb-8">
              In 2 minutes, see how your body is performing, and what to do next.
            </p>
            <Button onClick={() => setScreen(SCREENS.IDENTITY)} className="rounded-full h-14 px-10 text-base" data-testid="start-scan-btn">
              Start My Scan <ArrowRight size={20} className="ml-2" />
            </Button>
          </motion.div>
        );

      // ── 2. IDENTITY ──
      case SCREENS.IDENTITY:
        return (
          <motion.div {...fadeSlide} key="identity" className="min-h-screen p-6 flex flex-col">
            <div className="flex justify-center pt-4 mb-4"><Logo className="h-9" /></div>
            <p className="text-center text-xs text-muted-foreground mb-6">Step 1 of 3</p>
            <div className="max-w-md mx-auto flex-1 flex flex-col justify-center w-full">
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold mb-1">Let's get to know you</h2>
                <p className="text-muted-foreground text-sm">We'll send your personalized Coach360 report after the event.</p>
              </div>
              <div className="space-y-4 mb-6">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs mb-1 block">First Name *</Label>
                    <Input value={form.first_name} onChange={e => up('first_name', e.target.value)} placeholder="First" className="h-12 rounded-xl" data-testid="first-name-input" />
                  </div>
                  <div>
                    <Label className="text-xs mb-1 block">Last Name</Label>
                    <Input value={form.last_name} onChange={e => up('last_name', e.target.value)} placeholder="Last" className="h-12 rounded-xl" data-testid="last-name-input" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Email *</Label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input type="email" value={form.email} onChange={e => up('email', e.target.value)} placeholder="you@email.com" className="pl-11 h-12 rounded-xl" data-testid="email-input" />
                  </div>
                </div>
              </div>
              <Button onClick={submitIdentity} disabled={isLoading || !form.first_name.trim() || !form.email.trim()} className="w-full rounded-full h-13" data-testid="identity-submit-btn">
                {isLoading ? 'Setting up...' : 'Continue'} <ArrowRight size={18} className="ml-2" />
              </Button>
              <Button variant="ghost" onClick={() => setScreen(SCREENS.WELCOME)} className="mt-2 text-sm text-muted-foreground">Back</Button>
            </div>
          </motion.div>
        );

      // ── 3. SNAPSHOT ──
      case SCREENS.SNAPSHOT:
        return (
          <motion.div {...fadeSlide} key="snapshot" className="min-h-screen p-6 pb-32">
            <div className="flex justify-center pt-2 mb-2"><Logo className="h-8" /></div>
            <p className="text-center text-xs text-muted-foreground mb-4">Step 2 of 3</p>
            <div className="max-w-md mx-auto space-y-6">
              {/* Goal */}
              <div>
                <Label className="text-sm font-semibold mb-2 block">What's your primary wellness goal?</Label>
                <div className="flex flex-wrap gap-2">
                  {GOAL_OPTIONS.map(g => <Tile key={g.value} selected={form.goal === g.value} onClick={() => up('goal', g.value)}>{g.label}</Tile>)}
                </div>
              </div>
              {/* Energy */}
              <div>
                <Label className="text-sm font-semibold mb-2 block">Energy Level</Label>
                <div className="flex gap-2">
                  {[{v:'low',l:'Low'},{v:'moderate',l:'Moderate'},{v:'high',l:'High'}].map(o => <Tile key={o.v} selected={form.energy===o.v} onClick={()=>up('energy',o.v)} className="flex-1 text-center">{o.l}</Tile>)}
                </div>
              </div>
              {/* Stress */}
              <div>
                <Label className="text-sm font-semibold mb-2 block">Stress Level</Label>
                <div className="flex gap-2">
                  {[{v:'low',l:'Low'},{v:'moderate',l:'Moderate'},{v:'high',l:'High'}].map(o => <Tile key={o.v} selected={form.stress===o.v} onClick={()=>up('stress',o.v)} className="flex-1 text-center">{o.l}</Tile>)}
                </div>
              </div>
              {/* Sleep Quality */}
              <div>
                <Label className="text-sm font-semibold mb-2 block">Sleep Quality</Label>
                <div className="flex gap-2">
                  {SLEEP_OPTIONS.map(o => <Tile key={o.value} selected={form.sleep_quality===o.value} onClick={()=>up('sleep_quality',o.value)} className="flex-1 text-center">{o.label}</Tile>)}
                </div>
              </div>
              {/* Activity Level */}
              <div>
                <Label className="text-sm font-semibold mb-2 block">Activity Level</Label>
                <div className="flex gap-2">
                  {ACTIVITY_OPTIONS.map(o => <Tile key={o.value} selected={form.activity_level===o.value} onClick={()=>up('activity_level',o.value)} className="flex-1 text-center">{o.label}</Tile>)}
                </div>
              </div>
              {/* Mood */}
              <div>
                <Label className="text-sm font-semibold mb-2 block">Current Mood ({form.mood}/10)</Label>
                <MoodSelect value={form.mood} onChange={v => up('mood', v)} />
              </div>
              {/* Height + Weight */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs mb-1 block">Height</Label>
                  <Input value={form.height} onChange={e=>up('height',e.target.value)} placeholder="5ft 10in or 178cm" className="h-11 rounded-xl" data-testid="height-input" />
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Weight</Label>
                  <Input value={form.weight} onChange={e=>up('weight',e.target.value)} placeholder="165 lbs or 75 kg" className="h-11 rounded-xl" data-testid="weight-input" />
                </div>
              </div>
              {/* Known Condition */}
              <div>
                <Label className="text-sm font-semibold mb-2 block">Known Condition (Optional)</Label>
                <div className="flex flex-wrap gap-2">
                  {CONDITION_OPTIONS.map(o => <Tile key={o.value} selected={form.known_condition===o.value} onClick={()=>up('known_condition',o.value)}>{o.label}</Tile>)}
                </div>
              </div>
              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setScreen(SCREENS.IDENTITY)} className="rounded-full h-13 px-6">
                  <ArrowLeft size={18} />
                </Button>
                <Button
                  onClick={generateBriefing}
                  disabled={!form.goal || !form.sleep_quality || !form.energy}
                  className="flex-1 rounded-full h-13"
                  data-testid="generate-btn"
                >
                  Build My Digital Twin <Sparkles size={18} className="ml-2" />
                </Button>
              </div>
            </div>
          </motion.div>
        );

      // ── 4. PROCESSING ──
      case SCREENS.PROCESSING:
        return (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} key="processing"
            className="min-h-screen flex flex-col items-center justify-center p-6 bg-[#0a0f1a]"
          >
            {/* Scan line animation */}
            <div className="relative w-32 h-48 mb-8">
              <div className="absolute inset-0 rounded-2xl border border-cyan-500/20 overflow-hidden">
                <motion.div
                  className="absolute left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-cyan-400 to-transparent"
                  animate={{ top: ['5%', '95%', '5%'] }}
                  transition={{ duration: 2.5, repeat: Infinity, ease: 'linear' }}
                />
                <div className="w-full h-full flex items-center justify-center opacity-30">
                  <Activity size={64} className="text-cyan-400" />
                </div>
              </div>
              <motion.div className="absolute inset-0 rounded-2xl" animate={{ boxShadow: ['0 0 20px rgba(0,200,255,0.1)', '0 0 40px rgba(0,200,255,0.25)', '0 0 20px rgba(0,200,255,0.1)'] }} transition={{ duration: 2, repeat: Infinity }} />
            </div>

            <AnimatePresence mode="wait">
              <motion.p key={processingIdx} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                className="text-white/90 font-medium text-center max-w-xs"
              >
                {PROCESSING_MESSAGES[processingIdx]}
              </motion.p>
            </AnimatePresence>

            <div className="flex gap-2 mt-8">
              {PROCESSING_MESSAGES.map((_, i) => (
                <div key={i} className={`w-2 h-2 rounded-full transition-all ${i <= processingIdx ? 'bg-cyan-400' : 'bg-white/20'}`} />
              ))}
            </div>
          </motion.div>
        );

      // ── 5. DIGITAL TWIN REVEAL ──
      case SCREENS.TWIN_REVEAL:
        return (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} key="twin" className="min-h-screen p-6 flex flex-col">
            <div className="flex justify-center pt-2 mb-2"><Logo className="h-8" /></div>
            <p className="text-center text-xs text-muted-foreground mb-4">Step 3 of 3</p>

            <div className="max-w-md mx-auto flex-1 flex flex-col items-center justify-center w-full">
              {/* DTS Score Ring */}
              <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', delay: 0.2 }}
                className="relative w-44 h-44 mb-6"
              >
                <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                  <circle cx="50" cy="50" r="44" fill="none" stroke="hsl(var(--muted))" strokeWidth="6" opacity="0.3" />
                  <motion.circle
                    cx="50" cy="50" r="44" fill="none" stroke={getDtsColor(dts)} strokeWidth="6" strokeLinecap="round"
                    strokeDasharray={`${(dts / 100) * 276.46} 276.46`}
                    initial={{ strokeDasharray: '0 276.46' }}
                    animate={{ strokeDasharray: `${(dts / 100) * 276.46} 276.46` }}
                    transition={{ duration: 1.5, ease: 'easeOut' }}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <motion.span className="text-4xl font-black" style={{ color: getDtsColor(dts) }}
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}
                  >
                    {dts}
                  </motion.span>
                  <span className="text-xs text-muted-foreground mt-1">Digital Twin Score</span>
                </div>
              </motion.div>

              {/* Tooltip */}
              <div className="flex items-start gap-2 bg-primary/5 border border-primary/15 rounded-xl p-3 mb-6 max-w-xs">
                <Eye size={14} className="text-primary mt-0.5 shrink-0" />
                <p className="text-[11px] text-muted-foreground">This is your real-time health snapshot based on your inputs.</p>
              </div>

              {/* Quick vitals grid */}
              <div className="grid grid-cols-4 gap-3 w-full max-w-xs mb-8">
                {[
                  { label: 'Mood', value: `${form.mood}/10`, icon: Brain },
                  { label: 'Sleep', value: form.sleep_quality, icon: Moon },
                  { label: 'HR', value: `${vitals.heart_rate || '--'}`, icon: Heart },
                  { label: 'SpO2', value: `${vitals.spo2 || '--'}%`, icon: Activity },
                ].map((v, i) => (
                  <motion.div key={v.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 + i * 0.1 }}
                    className="text-center"
                  >
                    <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center mx-auto mb-1">
                      <v.icon size={16} className="text-primary" />
                    </div>
                    <p className="text-sm font-bold">{v.value}</p>
                    <p className="text-[10px] text-muted-foreground">{v.label}</p>
                  </motion.div>
                ))}
              </div>

              <Button onClick={() => { setScreen(SCREENS.COACH_MESSAGE); track('coach_viewed'); }} className="w-full max-w-xs rounded-full h-13" data-testid="see-plan-btn">
                See My Plan <ChevronRight size={18} className="ml-2" />
              </Button>
            </div>
          </motion.div>
        );

      // ── 6. COACH360 MESSAGE ──
      case SCREENS.COACH_MESSAGE:
        return (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} key="coach" className="min-h-screen p-6 pb-36">
            <div className="max-w-md mx-auto">
              <div className="text-center mb-6">
                <Badge className="bg-primary/10 text-primary border-0 mb-3 inline-flex items-center gap-1">
                  <Sparkles size={12} /> Coach360
                </Badge>
                <h1 className="text-2xl font-bold" data-testid="coach-greeting">
                  {form.first_name}'s Body Briefing
                </h1>
              </div>

              {/* Summary */}
              <Card className="p-5 mb-5 border-l-4 border-l-primary bg-card" data-testid="briefing-summary">
                <div className="flex items-center gap-2 mb-3">
                  <Brain size={16} className="text-primary" />
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Your Body Today</span>
                </div>
                <p className="text-sm leading-relaxed">{briefing.summary}</p>
              </Card>

              {/* Signals */}
              {briefing.signals?.length > 0 && (
                <Card className="p-5 mb-5 bg-card" data-testid="briefing-signals">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertCircle size={16} className="text-amber-500" />
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">What We're Noticing</span>
                  </div>
                  <ul className="space-y-2">
                    {briefing.signals.map((s, i) => (
                      <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                        <span className="text-amber-500 mt-1 shrink-0">&#8226;</span> {s}
                      </li>
                    ))}
                  </ul>
                </Card>
              )}

              {/* Actions */}
              {briefing.actions?.length > 0 && (
                <div className="mb-5">
                  <h3 className="font-medium text-sm mb-3 flex items-center gap-2">
                    <Target size={14} className="text-primary" /> Your Next Best Steps
                  </h3>
                  <div className="space-y-2.5">
                    {briefing.actions.map((a, i) => (
                      <Card key={i} className="p-4 bg-card" data-testid={`action-${i}`}>
                        <div className="flex items-start gap-3">
                          <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                            <span className="text-xs font-bold text-primary">{i + 1}</span>
                          </div>
                          <div>
                            <p className="font-medium text-sm">{a.title}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{a.description}</p>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Closing */}
              {briefing.closing && (
                <Card className="p-4 bg-gradient-to-br from-amber-500/5 to-primary/5 border-l-4 border-l-amber-500 mb-6" data-testid="briefing-closing">
                  <div className="flex items-center gap-2 mb-2">
                    <Star size={14} className="text-amber-500" />
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Coach360 Note</span>
                  </div>
                  <p className="text-sm">{briefing.closing}</p>
                </Card>
              )}

              {/* Send email button */}
              <Button
                onClick={sendReport}
                disabled={isLoading || emailSent}
                className="w-full rounded-full h-13 mb-3"
                data-testid="send-report-btn"
              >
                {emailSent ? <><Check size={18} className="mr-2" />Report Sent!</> : isLoading ? 'Sending...' : <><Send size={18} className="mr-2" />Send Full Report to My Email</>}
              </Button>

              <Button variant="outline" onClick={() => setScreen(SCREENS.CLOSE)} className="w-full rounded-full h-11 text-sm" data-testid="continue-close-btn">
                Continue <ArrowRight size={16} className="ml-1" />
              </Button>
            </div>
          </motion.div>
        );

      // ── 7. CLOSE ──
      case SCREENS.CLOSE:
        return (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} key="close" className="min-h-screen p-6 flex flex-col items-center justify-center text-center">
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring' }}
              className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-6"
            >
              <Check size={40} className="text-primary" />
            </motion.div>

            <h1 className="text-2xl font-bold mb-2" data-testid="close-title">You're on the list.</h1>
            <p className="text-muted-foreground text-sm max-w-xs mb-8">
              We'll send your full report + early access when HealthLink360 launches.
            </p>

            <Card className="p-4 bg-gradient-to-br from-primary/5 to-amber-500/5 mb-8 w-full max-w-xs">
              <div className="flex items-center gap-3">
                <Star size={18} className="text-amber-500" />
                <div className="text-left">
                  <p className="font-medium text-sm">Founding Member</p>
                  <p className="text-xs text-muted-foreground">Early access secured</p>
                </div>
                <Badge className="ml-auto bg-amber-500/10 text-amber-600 border-0">Active</Badge>
              </div>
            </Card>

            <div className="space-y-3 w-full max-w-xs">
              <Button onClick={async () => {
                try {
                  const r = await axios.post(`${API}/demo/auto-login`, { user_id: userId, session_token: sessionToken });
                  localStorage.setItem('healthlink-token', r.data.access_token);
                  axios.defaults.headers.common['Authorization'] = `Bearer ${r.data.access_token}`;
                  authUpdate(r.data.user);
                  navigate('/dashboard');
                } catch { toast.error('Could not load dashboard'); }
              }} className="w-full rounded-full h-13" data-testid="explore-dashboard-btn">
                Explore Demo Dashboard <ChevronRight size={18} className="ml-2" />
              </Button>
              <Button variant="outline" onClick={() => {
                setScreen(SCREENS.WELCOME);
                setForm(p => ({ ...p, first_name: '', last_name: '', email: '', goal: '', energy: '', mood: 5, sleep_quality: '', activity_level: '', known_condition: '', stress: '', height: '', weight: '' }));
                setResult(null);
                setEmailSent(false);
                setUserId(null);
                setSessionToken(null);
              }} className="w-full rounded-full h-11" data-testid="try-another-btn">
                <RefreshCw size={16} className="mr-2" /> Try Another Profile
              </Button>
            </div>

            <div className="flex items-center gap-2 mt-8">
              <Shield size={12} className="text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Your data is encrypted and private</p>
            </div>
          </motion.div>
        );

      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="demo-flow">
      <AnimatePresence mode="wait">{renderScreen()}</AnimatePresence>
    </div>
  );
}
